import { addCalendarDays } from "@common/helpers/date";
import { CloseApplicationType } from "@common/types/closeApplicationType";
import { CloseHealth, CloseHealthReason } from "@common/types/closeHealth";
import type { ClientsWorkOverviewWorkRow } from "@components/organizationView/clients-work/clientsWorkOverviewViewConfig";
import {
    DEMO_RUN_KINDS,
    type DemoDataset,
    type DemoRunRecord,
    type DemoViewerRecord,
} from "./busyClosesDemo.types";
import { buildDemoRun } from "./buildDemoRun";
import { DEMO_ACCOUNT_NAMES } from "./demoCopy";
import { getDemoServicePeriod, toDemoDaysAgoIso } from "./demoDates";
import { createDemoIdFactory, createDemoRandom } from "./demoRandom";
import {
    type DemoClient,
    type DemoNamedRef,
    type DemoPerson,
    toCloseOverviewMember,
} from "./demoSources";
import { getDemoTeam, getDemoViewerRecord, pickDemoLead } from "./demoTeam";

const CLOSE_COUNT = 14;
const MAX_CLIENTS = 7;
const RUN_CLOSE_INDEXES: readonly number[] = [0, 2, 5, 7, 10];
const WATCHING_CLOSE_INDEXES: readonly number[] = [4, 9];
const AT_RISK_CLOSE_INDEX = 6;

export interface DemoWorkSources {
    datasetKey: string;
    clients: readonly DemoClient[];
    people: readonly DemoPerson[];
    selfId: number | null;
    stages: readonly DemoNamedRef[];
    templates: readonly DemoNamedRef[];
    today: string;
    now: number;
}

export interface DemoWorkDataset extends DemoDataset {
    rows: readonly ClientsWorkOverviewWorkRow[];
}

const getHealth = (index: number) => {
    if (index === AT_RISK_CLOSE_INDEX) {
        return {
            health: CloseHealth.AT_RISK,
            healthReason: CloseHealthReason.DELIVERY_DATE_PASSED,
            deliveryInDays: { min: -4, max: -2 },
        };
    }
    if (WATCHING_CLOSE_INDEXES.includes(index)) {
        return {
            health: CloseHealth.WATCHING,
            healthReason: CloseHealthReason.STAGE_TASKS_DUE_TODAY,
            deliveryInDays: { min: 0, max: 1 },
        };
    }
    return {
        health: CloseHealth.ON_TRACK,
        healthReason: CloseHealthReason.STAGE_TASKS_DUE_LATER,
        deliveryInDays: { min: 2, max: 14 },
    };
};

/** About 14 seeded fake applications on the organization's real clients, stages, templates and team, with five runs; empty if any list is. */
export const buildDemoWorkDataset = (
    sources: DemoWorkSources,
): DemoWorkDataset => {
    const random = createDemoRandom(sources.datasetKey);
    const ids = createDemoIdFactory(sources.datasetKey);
    const clients = random
        .shuffle(sources.clients.filter((client) => client.entities.length > 0))
        .slice(0, MAX_CLIENTS);
    if (
        clients.length === 0 ||
        sources.stages.length === 0 ||
        sources.templates.length === 0
    ) {
        return { rows: [], runs: [], viewersByTargetId: new Map() };
    }
    const team = getDemoTeam(sources);
    const period = getDemoServicePeriod(sources.today);
    const rows: ClientsWorkOverviewWorkRow[] = [];
    const runs: DemoRunRecord[] = [];
    const viewersByTargetId = new Map<string, DemoViewerRecord>();

    for (let index = 0; index < CLOSE_COUNT; index++) {
        const client = clients[index % clients.length];
        const entity =
            client.entities[
                Math.floor(index / clients.length) % client.entities.length
            ];
        const stage = random.pick(sources.stages);
        const template = sources.templates[index % sources.templates.length];
        const preparer = pickDemoLead({ team, random, index });
        const reviewerCandidates = team.others.filter(
            (person) => person.id !== preparer.id,
        );
        const reviewer =
            reviewerCandidates.length > 0
                ? random.pick(reviewerCandidates)
                : preparer;
        const { health, healthReason, deliveryInDays } = getHealth(index);
        const totalTaskCount = random.int(6, 14);
        const terminatedTasksCount = random.int(0, totalTaskCount - 2);
        const closeUuid = ids("close", index);
        const runKindIndex = RUN_CLOSE_INDEXES.indexOf(index);
        const runKind =
            runKindIndex === -1 ? null : DEMO_RUN_KINDS[runKindIndex];
        const teamMembers = [preparer, reviewer].map(toCloseOverviewMember);

        rows.push({
            id: `entity:${entity.id}:close:${closeUuid}`,
            rowType: "work",
            name: template.name,
            entityId: entity.id,
            workspaceId: client.workspaceId,
            entityIconColor: entity.iconColor,
            entityLogo: entity.logo,
            entityName: entity.name,
            entityIsBusiness: true,
            workspaceName: client.name,
            closeId: `close:${closeUuid}`,
            startDate: period.startDate,
            endDate: period.endDate,
            workStartDate: addCalendarDays(sources.today, -random.int(3, 12)),
            workDeliveryDate: addCalendarDays(
                sources.today,
                random.int(deliveryInDays.min, deliveryInDays.max),
            ),
            closeStageId: stage.id,
            closeStageName: stage.name,
            closeTemplateId: template.id,
            recurrenceId: null,
            closeStageSetAt: toDemoDaysAgoIso({
                now: sources.now,
                days: random.int(0, 9) + 0.25,
            }),
            isFinished: false,
            cancelledAt: null,
            terminatedTasksCount,
            totalTaskCount,
            estimatedTimeRemaining:
                (totalTaskCount - terminatedTasksCount) * random.int(15, 45),
            estimatedTimeDone: terminatedTasksCount * random.int(15, 45),
            estimatedTimeByMember: [],
            preparers: [teamMembers[0]],
            reviewers: [teamMembers[1]],
            managers: [],
            openRoles: {
                preparer: { assigneeIds: [preparer.id], hasOpenTasks: true },
                reviewer: { assigneeIds: [reviewer.id], hasOpenTasks: true },
                manager: { assigneeIds: [], hasOpenTasks: false },
            },
            createdByUserId: preparer.id,
            createdAt: toDemoDaysAgoIso({ now: sources.now, days: 20 }),
            unreadTaskCount: random.int(0, 3) === 0 ? random.int(1, 2) : 0,
            applicationType: CloseApplicationType.AUTOMATIC,
            health,
            healthReason,
            isAgentRunning: runKind === "working",
        });
        viewersByTargetId.set(
            closeUuid,
            getDemoViewerRecord({ team, random, index }),
        );
        if (runKind !== null) {
            runs.push(
                buildDemoRun({
                    kind: runKind,
                    index: runKindIndex,
                    ids,
                    random,
                    now: sources.now,
                    runTaskId: ids("task", index),
                    taskTitle: `${template.name} for ${entity.name}`,
                    client,
                    entity,
                    close: {
                        id: closeUuid,
                        name: template.name,
                        startDate: period.startDate,
                        endDate: period.endDate,
                    },
                    closeTasks: {
                        done: terminatedTasksCount,
                        total: totalTaskCount,
                    },
                    startedBy: preparer,
                    reviewer,
                    month: period.monthName,
                    account: random.pick(DEMO_ACCOUNT_NAMES),
                }),
            );
        }
    }
    return { rows, runs, viewersByTargetId };
};
